import { Inject, Injectable } from "@angular/core";
import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpHeaders
} from "@angular/common/http";
import { ApiError, ApiOk } from "@youm-front/api/domain";
import { URLTrailingSlash } from "@youm-front/core";
import { Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { API_CONFIG_TOKEN, ApiConfig } from "../../providers";
import { apiErrorHttpStatusSet } from "./api-error-http-status";

@Injectable()
export class ApiService {
  constructor(
    private readonly _httpClient: HttpClient,
    @Inject(API_CONFIG_TOKEN) private readonly apiConfig: ApiConfig
  ) {}

  get<T>(
    path: string,
    version: "v1" = "v1",
    context?: HttpContext
  ): Observable<T> {
    const endpoint = this.getEndpointForVersion(version);

    const url = new URLTrailingSlash(path, endpoint);

    return this._httpClient
      .get<ApiOk<T>>(url.href, {
        context
      })
      .pipe(map(this.handleResponse), catchError(this.handleErrorResponse));
  }

  post<T, D = unknown>(
    path: string,
    data: D,
    version: "v1" = "v1",
    headers?: HttpHeaders,
    context?: HttpContext
  ): Observable<T> {
    const endpoint = this.getEndpointForVersion(version);

    const url = new URLTrailingSlash(path, endpoint);

    return this._httpClient
      .post<ApiOk<T>>(url.href, data, { headers, context })
      .pipe(map(this.handleResponse), catchError(this.handleErrorResponse));
  }

  put<T, D = unknown>(
    path: string,
    data: D,
    version: "v1" = "v1"
  ): Observable<T> {
    const endpoint = this.getEndpointForVersion(version);

    const url = new URLTrailingSlash(path, endpoint);

    return this._httpClient
      .put<ApiOk<T>>(url.href, data)
      .pipe(map(this.handleResponse), catchError(this.handleErrorResponse));
  }

  delete<T>(path: string, version: "v1" = "v1"): Observable<T> {
    const endpoint = this.getEndpointForVersion(version);

    const url = new URLTrailingSlash(path, endpoint);

    return this._httpClient
      .delete<ApiOk<T>>(url.href)
      .pipe(map(this.handleResponse), catchError(this.handleErrorResponse));
  }

  private handleResponse<T>(response: ApiOk<T>): T {
    if (response.code !== 0) {
      throw new Error(
        `Invalid response: ${response.code}; ${JSON.stringify(
          response.payload
        )}`
      );
    }

    return response.payload;
  }

  private handleErrorResponse(response: HttpErrorResponse): Observable<never> {
    // An empty body (e.g. a proxy 502) arrives as null; failure handlers read
    // `.message` on what is thrown, so throw an object instead.
    const error = response.error ?? {
      message: `HTTP ${response.status} with no body`
    };

    apiErrorHttpStatusSet(error, response.status);
    return throwError(error as ApiError);
  }

  private getEndpointForVersion(version: "v1"): string {
    if (version === "v1") {
      return new URLTrailingSlash(`api/${version}`, this.apiConfig.serverUrl)
        .href;
    }

    throw new Error("Incorrect API version");
  }
}
